'use client';

import { ChevronDown, Check, Search } from 'lucide-react';
import { useState, useRef, useEffect, ReactNode, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { useAnchoredPanel } from '@/lib/hooks/useAnchoredPanel';

interface CustomSelectOption {
  value: string;
  label: string;
}

interface CustomSelectProps {
  /**
   * Label for the select field
   */
  label?: string | ReactNode;
  /**
   * Current selected value
   */
  value: string;
  /**
   * Change handler
   */
  onChange: (value: string) => void;
  /**
   * Array of options
   */
  options: CustomSelectOption[];
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Show required asterisk
   */
  required?: boolean;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Additional CSS classes for the wrapper
   */
  wrapperClassName?: string;
  /**
   * DOM id applied to the wrapper (used for scroll-to-field targeting)
   */
  id?: string;
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Enable search/filter input inside the dropdown
   */
  searchable?: boolean;
  /**
   * Placeholder for the search input (only used when searchable is true)
   */
  searchPlaceholder?: string;
}

/**
 * Custom dropdown select with text wrapping support for long options
 *
 * Perfect for mobile devices where native select dropdowns don't wrap text.
 * Handles very long text like Ukrainian medical terminology gracefully.
 *
 * @example
 * <CustomSelect
 *   label="Event Direction"
 *   value={eventDirection}
 *   onChange={setEventDirection}
 *   options={EVENT_DIRECTIONS.map(d => ({ value: d, label: d }))}
 *   placeholder="Select direction"
 * />
 */
export default function CustomSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  required = false,
  error,
  wrapperClassName = '',
  id,
  disabled = false,
  searchable = false,
  searchPlaceholder = 'Search...',
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panel = useAnchoredPanel(anchorRef, 300);

  const openMenu = () => { setIsOpen(true); panel.open(); };
  const closeMenu = () => { setIsOpen(false); setSearch(''); panel.close(); };
  const closePanel = panel.close;

  // Close dropdown when clicking outside (the list itself lives in a portal)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setIsOpen(false);
      setSearch('');
      closePanel();
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, closePanel]);

  // Focus search input and scroll selected into view when dropdown opens
  useEffect(() => {
    if (isOpen) {
      if (searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
      if (dropdownRef.current) {
        const selectedOption = dropdownRef.current.querySelector('[data-selected="true"]');
        if (selectedOption) {
          selectedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    }
  }, [isOpen, searchable]);

  // Filter options when searching
  const filteredOptions = useMemo(() => {
    if (!searchable || !search) return options;
    const query = search.toLowerCase();
    return options.filter(
      (opt) => opt.value === '' || opt.label.toLowerCase().includes(query)
    );
  }, [options, search, searchable]);

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  return (
    <div id={id} className={`min-w-0 ${wrapperClassName}`} ref={wrapperRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1 break-words">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative" ref={anchorRef}>
        {/* Trigger Button */}
        <Button
          type="button"
          variant="outline"
          onClick={() => { if (disabled) return; if (isOpen) closeMenu(); else openMenu(); }}
          disabled={disabled}
          className={`
            w-full h-10 text-left justify-start
            px-4 py-2 pr-12
            border
            ${error ? 'border-red-500' : 'border-gray-300'}
            bg-white
            ${value ? 'text-gray-900' : 'text-gray-500'}
            text-sm font-normal
            cursor-pointer
            focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500
            hover:border-gray-400 hover:bg-white
            disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500
            truncate
          `.trim().replace(/\s+/g, ' ')}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {displayText}
        </Button>

        {/* Chevron Icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:pr-3 pointer-events-none">
          <ChevronDown
            className={`h-4 w-4 sm:h-5 sm:w-5 transition-all ${disabled ? 'text-gray-400' : 'text-gray-500'
              } ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>

        {/* Dropdown Menu: portalled and placed on screen, so a scrolling form or dialog cannot clip it */}
        {isOpen && panel.style && createPortal(
          <div
            ref={panelRef}
            style={panel.style}
            className="z-[80] flex flex-col bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden"
            role="listbox"
          >
            {/* Search input */}
            {searchable && (
              <div className="shrink-0 bg-white border-b border-gray-200 p-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.stopPropagation();
                        if (search) {
                          setSearch('');
                        } else {
                          closeMenu();
                        }
                      }
                    }}
                    placeholder={searchPlaceholder}
                    className="w-full pl-8 pr-3 py-2 text-base sm:text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    autoComplete="off"
                  />
                </div>
              </div>
            )}

            {/* Options list */}
            <div ref={dropdownRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  No results found
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = option.value === value;
                  // Hide the empty-value placeholder option while searching
                  if (search && option.value === '') return null;
                  return (
                    <Button
                      key={option.value}
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        onChange(option.value);
                        closeMenu();
                      }}
                      data-selected={isSelected}
                      className={`
                        w-full h-auto text-left justify-start rounded-none px-3 sm:px-4 py-2.5 sm:py-3
                        flex items-start gap-2
                        text-sm
                        ${isSelected
                          ? 'bg-blue-50 text-blue-700 font-medium hover:bg-blue-50'
                          : 'text-gray-900 hover:bg-gray-50 font-normal'
                        }
                      `.trim().replace(/\s+/g, ' ')}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <span className="flex-1 break-words whitespace-normal leading-relaxed">
                        {option.label}
                      </span>
                      {isSelected && (
                        <Check className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      )}
                    </Button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
      </div>

      {error && (
        <p className="mt-1 text-xs sm:text-sm text-red-600 break-words" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
